// Receive a box that has no purchase order behind it: a sample, a warranty
// replacement, a card purchase, or a line of a request (requisitionPrefill).
// Goods on a purchase order are received on the order's own page
// (ReceiveOrderPage), never here.
//
// THE QUANTITY IS BLANK AND NOTHING BUT A PERSON FILLS IT. The request's asked-for
// count is shown beside it as a claim, and its chip makes "all of it arrived" a
// one-click ACT rather than a default nobody noticed. See the state declaration
// for what a prefill once cost us.
//
// POSTs to /api/inventory/receive. On success the label opens in a tab and the
// parent refreshes its feeds.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { Select } from '../components/ui/Select';
import { Alert } from '../components/ui/Alert';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import { Panel } from '../components/ui/Panel';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { openAuthedPdf } from './openAuthedPdf';
import ReceiverIdentityModal from './ReceiverIdentityModal';
import { receiverErrorMessage } from './receivedAs';
import NewItemModal from '../Parts/NewItemModal';
import useUserStore from '../stores/useUserStore';
import EntityCombobox, { type ComboboxOption } from '../components/EntityCombobox';
import ItemName, { itemLabel } from '../components/ItemName';
import ItemPicker from '../components/ItemPicker';
import { modalLabel } from '../components/review/fieldStyles';
import {
  errorMessage,
  useRequisitionsApi,
  type CostSource,
  type RequisitionDetail,
} from '../Requisitions/requisitionsApi';
import type { PartsCatalogRow, LocationRow, ReceiveResult } from './types';

/**
 * A request line going into stock. The item and the no-PO reason are locked to
 * the request, and after the receive the box is recorded on the line. The server refuses that record unless the box's notes begin
 * `no-PO: requisition <req_number>`, so the notes prefix is load-bearing.
 */
export interface RequisitionPrefill {
  requisition_id: string;
  line_id: string;
  req_number: string;
  part_catalog_id: number;
  /** What was asked for. A claim beside the count, never the count itself. */
  quantity: number | null;
  unit_cost_cents: number | null;
  /** Null: no receipt line matched and no estimate, so the cost starts empty. */
  cost_source: CostSource | null;
  /** The line's catalog item, for the locked item block and the toast. */
  part?: { sku: string; name: string | null } | null;
}

interface Props {
  requisitionPrefill?: RequisitionPrefill | null;
  onClose: () => void;
  onReceived: (result: ReceiveResult) => void;
  /** The request as it stands once the box is recorded on its line. Requisition path only. */
  onRecorded?: (requisition: RequisitionDetail) => void;
  /** Tells the shell this drawer sits in whether typed work would be lost. */
  onDirtyChange?: (dirty: boolean) => void;
}

// `block` labels wrap their input so the association is programmatic — screen
// readers announce the label when the field gets focus, no htmlFor needed.
const labelClass = modalLabel;

// THREE GROUPS, NOT EIGHT FIELDS.
//
// The drawer used to be four identical two-column rows, every field the same
// weight, nothing telling the eye where to land. The questions it actually asks
// are: what arrived, how much of it, and where did it go. Grouping to that costs
// nothing and removes the "which box am I in" pause at the bench.
const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({
  title,
  hint,
  children,
}) => (
  <section className="space-y-3">
    <div className="flex items-baseline gap-2">
      <h3 className="text-[11px] uppercase tracking-wider text-on-surface-variant font-semibold">{title}</h3>
      {hint && <span className="text-[11px] text-on-surface-variant">{hint}</span>}
      <div className="h-px flex-1 bg-outline-variant" />
    </div>
    {children}
  </section>
);

// The honest set of reasons a box arrives with no purchase order behind it.
// Free text was the alternative and would have been unanalysable — the point of
// asking is to be able to COUNT these later and decide whether the door stays
// open, which needs a closed vocabulary.
//
// "Found in the shop" is deliberately absent: that is a count correction, not an
// arrival, and it belongs in Adjust. Minting a receipt for it invents a delivery
// that never happened, with a date.
const NO_PO_REASONS = [
  { value: 'sample', label: 'Free sample or evaluation unit' },
  { value: 'warranty', label: 'Warranty or RMA replacement' },
  { value: 'card', label: 'Bought on a card, no PO raised' },
  { value: 'customer_supplied', label: 'Supplied by a customer' },
  { value: 'backfill', label: 'Historical stock being entered' },
  { value: 'other', label: 'Something else (say so in Notes)' },
  // Set only through requisitionPrefill, never picked: the request number has to
  // follow it in the notes, and only the request page knows it.
  { value: 'requisition', label: 'Bought on a request' },
] as const;

const PICKABLE_NO_PO_REASONS = NO_PO_REASONS.filter((r) => r.value !== 'requisition');

const todayIso = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const ReceiveDrawer: React.FC<Props> = ({
  requisitionPrefill,
  onClose,
  onReceived,
  onRecorded,
  onDirtyChange,
}) => {
  const api = useApi();
  const reqApi = useRequisitionsApi();
  const toast = useToast();
  const forRequest = requisitionPrefill || null;

  // ── THE QUANTITY STARTS EMPTY, ON EVERY PATH ─────────────────────────────
  //
  // It used to open pre-filled: the slip's outstanding quantity if a packing list
  // had been read, otherwise the order's remaining balance. On 2026-07-23 that
  // put 108 in the field (a line of 150 with 42 already received), the label
  // beside it read "expected 108 more", and 20 boards were on the bench. Pressing
  // Receive was the path of least resistance, so 88 units of stock that do not
  // exist went onto the shelf and the line closed with 88 still owed.
  //
  // NEITHER REFERENCE ERP JUSTIFIES THE PREFILL AS WE HAD IT. ERPNext does fill
  // the remaining quantity, but into a document grid you then review and Submit.
  // Odoo is the closer shape and the more useful lesson: it fills the demand
  // quantity and then requires a SEPARATE explicit act (`picked` on the move
  // line, or the "Set quantities" button) before that number counts. The
  // affirmation is the control, not the number.
  //
  // So: blank. On a request line the asked-for count stays one click away via
  // the chip below, shown as a claim next to the thing it is a claim about.
  // Nothing populates this field except a person.
  const [quantity, setQuantity] = useState<string>('');
  const [lotNumber, setLotNumber] = useState('');
  const [manufactureDate, setManufactureDate] = useState<string>(todayIso);
  const [notes, setNotes] = useState('');
  const [locationId, setLocationId] = useState<string>('');
  const [locations, setLocations] = useState<LocationRow[]>([]);
  // The location the drawer preselected on its own, which is not typed work.
  const [openedLocationId, setOpenedLocationId] = useState('');
  const [openedDate] = useState(todayIso);
  // Distinguishes "still fetching" from "fetched, and there are none" — the
  // second is Swarm's opening state and needs a sentence, not an empty picker.
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  // Backdating is admin-only (the API 403s otherwise), so the field is not
  // rendered at all for anyone else rather than shown and then refused.
  const user = useUserStore((s) => s.user);
  const isAdmin = !!(user?.isadmin || user?.can_admin);
  const [receivedAt, setReceivedAt] = useState('');
  // Inline catalog mint, opened from the picker's "New item…" row.
  //
  // The typed text rides along. Somebody who typed "M3x8 standoff", found
  // nothing and pressed the create row has already said what the thing is; the
  // wizard opening blank asks them to say it again, which is the retyping the
  // in-place create exists to remove.
  const [mintOpen, setMintOpen] = useState(false);
  const [mintName, setMintName] = useState('');
  const [noPoReason, setNoPoReason] = useState(forRequest ? 'requisition' : '');
  /**
   * The box is in stock but the request did not record it. The form is gone at
   * this point on purpose: receiving again would put a second box on the shelf.
   */
  const [unrecorded, setUnrecorded] = useState<{ result: ReceiveResult; message: string } | null>(null);
  const [recording, setRecording] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── REJECTION AT THE DOOR (037) ────────────────────────────────────────────
  //
  // Collapsed by default, and staying that way. Almost every delivery is fine,
  // and a rejected-quantity box sitting open beside the real one is a field to
  // tab past a hundred times for the once it matters. Same reasoning that
  // demoted manual receive to a quiet link rather than deleting it.
  //
  // The quantity above stays the TOTAL that arrived. That is the number the
  // person is holding the box and counting, and asking them to subtract before
  // typing is how you get 34 entered when 40 turned up.
  const [rejecting, setRejecting] = useState(false);
  const [rejectedQty, setRejectedQty] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionResolution, setRejectionResolution] =
    useState<'return_to_vendor' | 'scrap'>('return_to_vendor');

  /**
   * The split, shown live so nobody has to do the subtraction. null while the
   * numbers do not yet make sense, which is what keeps the hint from flashing
   * "40 into stock, NaN to QC Hold" as somebody types.
   *
   * A whole-delivery rejection is not offered here: the API refuses it and
   * points at the two-step path, so letting the field reach it would only
   * produce a rejection at submit time.
   */
  const rejectedSplit = useMemo(() => {
    if (!rejecting) return null;
    const total = parseInt(quantity, 10);
    const bad = parseInt(rejectedQty, 10);
    if (!Number.isInteger(total) || !Number.isInteger(bad)) return null;
    if (bad <= 0 || bad >= total) return null;
    return { accepted: total - bad, rejected: bad };
  }, [rejecting, quantity, rejectedQty]);

  // The built body, held while the identity step asks who is receiving it.
  const [pendingBody, setPendingBody] = useState<Record<string, unknown> | null>(null);
  const [identityError, setIdentityError] = useState<string | null>(null);

  // The search, the debounce and the results list used to live here. They are
  // ItemPicker's now: one item lookup, one set of keyboard rules, one "New
  // item…" row, everywhere an item is chosen.
  const [selectedPart, setSelectedPart] = useState<PartsCatalogRow | null>(null);
  const [vendor, setVendor] = useState('');
  const [purchaseOrderRef, setPurchaseOrderRef] = useState('');
  const [unknownPoRef, setUnknownPoRef] = useState(false);
  const lastCheckedPoRef = useRef('');
  const poRefSeq = useRef(0);
  const [unitCostUsd, setUnitCostUsd] = useState(
    forRequest && forRequest.unit_cost_cents !== null ? (forRequest.unit_cost_cents / 100).toFixed(2) : ''
  );
  // Stable idempotency token so retries after a network
  // hiccup don't dupe the row. Regenerated on every fresh drawer mount.
  const clientRequestId = useRef<string>(
    `cr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );

  // Fetch the RECEIVABLE locations once on mount.
  //
  // Was `?type=warehouse`. Now `?receivable=1`, which is warehouses PLUS vendor
  // premises that have been switched on — a contract assembler holding our
  // material is a real destination, and filtering by type alone could not
  // express that. The endpoint also excludes retired rows, which the old query
  // did not: a location you had taken out of service kept appearing here.
  //
  // Cancellation guard: drawers mount/unmount rapidly during a receiving
  // session; without it, late responses fire setState on unmounted components.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = (await api.get('/locations?receivable=1')) as LocationRow[];
        if (cancelled) return;
        setLocations(rows || []);
        setLocationsLoaded(true);
        // Preselect ONLY when there is exactly one inventory location (the
        // receivable list is inventory places plus vendors), matching the
        // server's own default (receipt-service resolveDefaultLocationId). With
        // two, picking one for the user is how stock lands in the wrong
        // building; with none, there is nothing to pick and the empty state
        // below says what to do about it.
        const warehouses = (rows || []).filter((l) => l.type !== 'vendor');
        if (warehouses.length === 1 && !locationId) {
          setLocationId(String(warehouses[0].id));
          setOpenedLocationId(String(warehouses[0].id));
        }
      } catch (err: any) {
        if (cancelled) return;
        setLocationsLoaded(true);
        toast.error(err?.message || 'Failed to load locations');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A hint, never a gate: canSubmit does not read unknownPoRef.
  const checkPoRef = async () => {
    const typed = purchaseOrderRef.trim();
    if (typed === lastCheckedPoRef.current) return;
    lastCheckedPoRef.current = typed;
    const seq = ++poRefSeq.current;
    if (!/^po-/i.test(typed)) {
      setUnknownPoRef(false);
      return;
    }
    try {
      const res = (await api.get(
        `/inventory/po-exists?number=${encodeURIComponent(typed)}`
      )) as { exists?: unknown };
      if (seq !== poRefSeq.current) return;
      if (typeof res?.exists !== 'boolean') throw new Error('unexpected shape');
      setUnknownPoRef(!res.exists);
    } catch {
      if (seq !== poRefSeq.current) return;
      // A lookup that did not run says nothing, so a failure never reads as "no order".
      setUnknownPoRef(false);
      lastCheckedPoRef.current = '';
    }
  };

  // Bumping the sequence on unmount drops a lookup still in flight.
  useEffect(() => () => {
    poRefSeq.current += 1;
  }, []);

  const initialUnitCost =
    forRequest && forRequest.unit_cost_cents !== null ? (forRequest.unit_cost_cents / 100).toFixed(2) : '';
  const dirty =
    !unrecorded &&
    (quantity.trim() !== '' ||
      lotNumber.trim() !== '' ||
      manufactureDate !== openedDate ||
      notes.trim() !== '' ||
      locationId !== openedLocationId ||
      receivedAt !== '' ||
      noPoReason !== (forRequest ? 'requisition' : '') ||
      selectedPart !== null ||
      vendor.trim() !== '' ||
      purchaseOrderRef.trim() !== '' ||
      unitCostUsd.trim() !== initialUnitCost ||
      rejectedQty.trim() !== '' ||
      rejectionReason.trim() !== '' ||
      (rejecting && rejectionResolution !== 'return_to_vendor'));

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gone means clean: the shell must not keep asking about a drawer that is closed.
  useEffect(() => () => onDirtyChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    const q = parseInt(quantity, 10);
    if (!Number.isFinite(q) || q <= 0) return false;
    if (!selectedPart && !forRequest) return false;
    // Required, not optional: an exception nobody has to justify is not an
    // exception, it is a second main path.
    if (!noPoReason) return false;
    return true;
  }, [submitting, quantity, selectedPart, noPoReason, forRequest]);

  // Warehouses first, then vendor premises under their own divider — the
  // common case should be the first thing under the cursor, and "our shelf" is
  // the common case. The chip is what stops two vendors with similar names, or
  // a vendor and a warehouse, being mistaken for each other.
  const locationOptions: ComboboxOption[] = useMemo(
    () =>
      locations.map((l) => ({
        id: l.id,
        label: l.name,
        sublabel: l.type === 'vendor' ? 'Holds our stock, not ours to build from' : null,
        chip: l.type === 'vendor' ? l.vendor_code || 'vendor' : null,
        group: l.type === 'vendor' ? 'Vendor premises' : 'Our locations',
      })),
    [locations]
  );

  const selectedLocation = useMemo(
    () => locations.find((l) => String(l.id) === locationId) || null,
    [locations, locationId]
  );

  // A whole number only: the receive takes whole units, and a request for 2.5
  // of something is not a count anyone can claim with one press.
  const requestedCount =
    forRequest && forRequest.quantity !== null && Number.isInteger(forRequest.quantity) && forRequest.quantity > 0
      ? forRequest.quantity
      : null;

  const costNote: { text: string; tone: 'warn' | 'plain' } | null = (() => {
    if (!forRequest || forRequest.unit_cost_cents === null) return null;
    if (forRequest.cost_source === 'estimate') {
      return { text: "This is the requester's estimate. Check it against the receipt.", tone: 'warn' };
    }
    if (forRequest.cost_source === 'receipt') return { text: 'From the card receipt.', tone: 'plain' };
    return null;
  })();

  const forRequestItem = forRequest?.part
    ? { base_name: forRequest.part.name, sku: forRequest.part.sku }
    : null;

  /** Ties the new box to the request line. False leaves the drawer on the retry panel. */
  const recordOnRequest = async (result: ReceiveResult): Promise<boolean> => {
    if (!forRequest) return true;
    setRecording(true);
    try {
      const updated = await reqApi.markLineReceived(
        forRequest.requisition_id,
        forRequest.line_id,
        result.receipt_item_id
      );
      setUnrecorded(null);
      onRecorded?.(updated);
      return true;
    } catch (err) {
      setUnrecorded({
        result,
        message: errorMessage(err, 'Check your connection and try again.'),
      });
      return false;
    } finally {
      setRecording(false);
    }
  };

  const submitReceive = async (body: Record<string, unknown>) => {
    setSubmitting(true);
    try {
      const result = (await api.post('/inventory/receive', { body })) as ReceiveResult;
      const q = Number(body.quantity_received);
      if (result.duplicate) {
        toast.info(`Already received as ${result.barcode}, so nothing new went in.`);
      } else {
        // The words, not the code: this toast is read at a glance beside the boxes.
        const itemName = selectedPart
          ? itemLabel(selectedPart)
          : forRequestItem
            ? itemLabel(forRequestItem)
            : '';
        toast.success(`Received ${q} ${itemName}`);
      }
      // Refresh parent feeds first — the server-of-record write is already
      // committed, so the user should see the inbox/recent-receipts update
      // immediately rather than wait on the PDF render. Fire-and-forget the
      // label preview: if it 401s or the server's lazy-render takes seconds,
      // we don't want the drawer to sit in "Receiving…" the whole time.
      // No setSubmitting(false) on the success path — onReceived unmounts
      // the drawer, and post-unmount setState triggers React's leak warning.
      openAuthedPdf(result.label_pdf_url).catch((pdfErr) => {
        console.error('Failed to open label PDF', pdfErr);
        toast.warning('Box received, but the label preview failed to open');
      });
      if (forRequest && !(await recordOnRequest(result))) {
        setPendingBody(null);
        setSubmitting(false);
        return;
      }
      onReceived(result);
    } catch (err: any) {
      const receiverError = receiverErrorMessage(err);
      if (receiverError) {
        // The identity step stays open with the refusal in it.
        const { received_as: _receivedAs, ...withoutIdentity } = body;
        setPendingBody(withoutIdentity);
        setIdentityError(receiverError);
        setSubmitting(false);
        return;
      }
      setPendingBody(null);
      toast.error(err?.message || 'Failed to receive box');
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const q = parseInt(quantity, 10);
    if (!Number.isInteger(q) || q <= 0) {
      toast.error('Quantity must be a positive whole number');
      return;
    }

    // Rejection validation, client side, so the drawer says it rather than the
    // server. The API enforces all three again; this is about not making
    // somebody submit to find out.
    let rejectionFields: Record<string, unknown> = {};
    if (rejecting) {
      const bad = parseInt(rejectedQty, 10);
      if (!Number.isInteger(bad) || bad <= 0) {
        toast.error('How many are bad? It has to be a whole number above zero.');
        return;
      }
      if (bad >= q) {
        toast.error(
          bad === q
            ? 'Rejecting the whole delivery is not supported here yet. Receive it, then reject the box from its box page, which ends in the same place.'
            : 'You cannot reject more units than arrived.'
        );
        return;
      }
      if (!rejectionReason.trim()) {
        toast.error('Say what is wrong with them. The vendor conversation and the credit note both need it.');
        return;
      }
      rejectionFields = {
        quantity_rejected: bad,
        rejection_reason: rejectionReason.trim(),
        rejection_resolution: rejectionResolution,
      };
    }

    if (!selectedPart && !forRequest) return;
    const costCents = unitCostUsd.trim()
      ? Math.round(parseFloat(unitCostUsd) * 100)
      : null;
    if (costCents !== null && (!Number.isFinite(costCents) || costCents < 0)) {
      toast.error('Unit cost must be a non-negative number');
      return;
    }
    const body: Record<string, unknown> = {
      ...(forRequest ? { part_catalog_id: forRequest.part_catalog_id } : { sku: selectedPart!.sku }),
      quantity_received: q,
      manufacture_date: manufactureDate || null,
      lot_number: lotNumber || null,
      vendor: vendor || null,
      purchase_order_ref: purchaseOrderRef || null,
      unit_cost_cents: costCents,
      location_id: locationId ? parseInt(locationId, 10) : null,
      // Omitted entirely unless an admin set one. The API 403s a
      // non-admin who sends it, so sending an empty string would turn
      // an ordinary receive into a refusal.
      ...(isAdmin && receivedAt ? { received_at: receivedAt } : {}),
      ...rejectionFields,
      // Prefixed into notes rather than a new column: receipt_items.notes
      // already exists and is shown on the box detail, and one more
      // migration to store a six-value enum is not worth it until somebody
      // wants to REPORT on these. When that day comes it is a backfill from
      // this prefix, which is why the format is fixed rather than prose.
      // The request number rides right after the reason, and the request's
      // `received` call checks for exactly that prefix.
      notes: [
        forRequest ? `no-PO: ${noPoReason} ${forRequest.req_number}` : `no-PO: ${noPoReason}`,
        notes.trim() || null,
      ].filter(Boolean).join(' — '),
      client_request_id: clientRequestId.current,
    };

    setIdentityError(null);
    setPendingBody(body);
  };

  return (
    <>
    {unrecorded && forRequest ? (
      <div className="space-y-4">
        <Alert
          tone="warning"
          live="alert"
          title={<>Box <span className="font-mono">{unrecorded.result.barcode}</span> is in stock</>}
        >
          {forRequest.req_number} did not record it: {unrecorded.message} Do not receive it
          again. Try recording it once more, or close and tell whoever looks after requests.
        </Alert>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onReceived(unrecorded.result)}
            className={buttonClass('tonal', 'md')}
          >
            Close
          </button>
          <button
            type="button"
            disabled={recording}
            onClick={async () => {
              const { result } = unrecorded;
              if (await recordOnRequest(result)) {
                toast.success(`Recorded on ${forRequest.req_number}.`);
                onReceived(result);
              }
            }}
            className={buttonClass('filled', 'md')}
          >
            {recording ? 'Recording…' : 'Record it on the request'}
          </button>
        </div>
      </div>
    ) : (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* THE ITEM: settled by the request, chosen otherwise.
          THE DELIBERATE EXCEPTION to name-first: `showSku` keeps the code under
          the name, muted. The person here is matching a carton against a
          printed label, and that carries the code, not the name. */}
      {forRequest ? (
        <div data-testid="locked-item">
          <Panel label="Item" padding="sm">
            <span className={labelClass}>Item</span>
            {forRequestItem ? (
              <ItemName size="md" showSku item={forRequestItem} />
            ) : (
              <p className="text-sm text-on-surface">Catalog item {forRequest.part_catalog_id}</p>
            )}
          </Panel>
        </div>
      ) : (
        /* THE DEAD END STAYS REMOVED, and is now the picker's own affordance.
           This used to be a hand-rolled typeahead with a "nothing matches" panel
           bolted under it. ItemPicker carries both, plus arrow keys and the
           pinned "New item…" row, so every lane that chooses an item behaves the
           same way. Minting still lands back here with the item selected, which
           is the whole reason the dead end went. */
        <ItemPicker
          label="Part"
          id="receive-item"
          value={selectedPart}
          onSelect={setSelectedPart}
          // Retired items included (and marked so in the row): a box of a part we
          // stopped buying still lands, and a picker that cannot find it offers to
          // mint a duplicate instead.
          extraParams={{ include_inactive: 'true' }}
          onCreate={(query) => {
            setMintName(query);
            setMintOpen(true);
          }}
          autoFocus
          placeholder="Search by name, SKU or part number"
        />
      )}

      {/* WHY THERE IS NO PURCHASE ORDER.
          Manual receive bypasses the approval threshold, the vendor-hold check
          and the spend record, so it is a real control gap — chunk 2's
          three-way match will trip over every box that came in this way.
          Recording the reason turns a silent bypass into a counted exception,
          which is also the data that says whether to keep this door open.

          ERPNext makes the same question a POLICY rather than a design
          decision: Buying Settings.po_required, enforced per line, with a
          per-supplier override. We have no such switch yet; asking is the
          cheap half of it. */}
      {forRequest && (
        <Panel label="No purchase order" padding="sm">
          <span className={labelClass}>No purchase order</span>
          <p className="text-sm text-on-surface">
            Bought on request <span className="font-mono">{forRequest.req_number}</span>
          </p>
        </Panel>
      )}
      {!forRequest && (
        <label className="block">
          <span className={labelClass}>Why is there no purchase order?</span>
          <Select
            label="Why is there no purchase order?"
            value={noPoReason}
            onChange={setNoPoReason}
            options={[
              { key: '', label: 'Pick one…' },
              ...PICKABLE_NO_PO_REASONS.map((r) => ({ key: r.value, label: r.label })),
            ]}
          />
          <p className="text-xs text-on-surface-variant mt-1 leading-snug">
            Most arrivals should come through the expected list above. If this one has a
            purchase order, close this and receive it from there instead.
          </p>
        </label>
      )}

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={labelClass}>Vendor</span>
          <Field
            thumb
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            placeholder="Optional"
          />
        </label>
        <div>
          <label className="block">
            <span className={labelClass}>Purchase order ref</span>
            <Field
              thumb
              value={purchaseOrderRef}
              aria-describedby={unknownPoRef ? 'receive-po-ref-hint' : undefined}
              onChange={(e) => {
                setPurchaseOrderRef(e.target.value);
                if (!e.target.value.trim()) {
                  setUnknownPoRef(false);
                  lastCheckedPoRef.current = '';
                  poRefSeq.current += 1;
                }
              }}
              onBlur={() => { void checkPoRef(); }}
              placeholder="Optional"
            />
          </label>
          {/* Outside the label so it stays out of the field's accessible name. */}
          <p
            id="receive-po-ref-hint"
            aria-live="polite"
            className="text-xs text-on-surface-variant mt-1 leading-snug empty:hidden"
          >
            {unknownPoRef
              ? 'No order with this number. Receiving without a PO is fine; check the number if one exists.'
              : ''}
          </p>
        </div>
      </div>

      {/* ── HOW MANY ─────────────────────────────────────────────────── */}
      <Section title="How many">
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          {/* THE QUESTION, NOT THE FIELD NAME. "Actual qty received" describes a
              database column; the person holding the box is being asked to count.
              It also removes the word "expected" from the label entirely, which
              is what the old "(expected 108 more)" suffix put right beside an
              input that already contained 108. */}
          <span className={labelClass}>How many actually turned up?</span>
          <Field
            size="lg"
            className="text-lg"
            type="number"
            min={1}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="Count them"
            // A WHEEL OVER A FOCUSED NUMBER INPUT STEPS ITS VALUE, in Chrome and
            // Firefox, and swallows the scroll so the page does not move and the
            // change is easy to miss. This field is focused on open (below) and the
            // form is three sections tall, so scrolling with the pointer still over
            // it is the ordinary next motion. Turning 20 into 19 with no keystroke
            // is precisely the failure this whole change exists to prevent, on the
            // one field where it costs money.
            //
            // blur() rather than preventDefault(): the page should still scroll.
            onWheel={(e) => e.currentTarget.blur()}
            // The one field on this form worth landing in. Locations and costs
            // are inherited or defaulted; this one is only ever known by the
            // person at the boxes.
            //
            // NOT React's `autoFocus`, which does not work inside DrawerShell:
            // the shell's own focus effect runs on a later tick and moves focus
            // to the first tabbable element, which is the × button. This
            // attribute is the shell's documented opt-out.
            data-drawer-autofocus
          />
        </label>
        {/* OUTSIDE the label above: text inside a wrapping label becomes part of
            the count field's accessible name. */}
        {forRequest && requestedCount !== null && (
          <div className="col-span-2 -mt-1 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setQuantity(String(requestedCount))}
              className={`${buttonClass('accent', 'sm')} tabular-nums`}
            >
              All {requestedCount} on the request
            </button>
            <span className="text-[11px] text-on-surface-variant">
              {forRequest.req_number} asked for {requestedCount}. Count what is in the box.
            </span>
          </div>
        )}
        <div>
        <label className="block">
          <span className={labelClass}>Unit cost ($)</span>
          <Field
            thumb
            type="number"
            step="0.01"
            min={0}
            value={unitCostUsd}
            onChange={(e) => setUnitCostUsd(e.target.value)}
            placeholder="Optional"
            aria-describedby={costNote ? 'receive-cost-note' : undefined}
          />
        </label>
        {/* Outside the label so it stays out of the field's accessible name. An
            estimate that went in unread would move the weighted average on a
            guess, so it is said where the number is. */}
        <Reveal show={!!costNote}>
          <p
            id="receive-cost-note"
            className={`mt-1 text-xs leading-snug ${
              costNote?.tone === 'warn' ? 'text-amber-300' : 'text-on-surface-variant'
            }`}
          >
            {costNote?.text}
          </p>
        </Reveal>
        </div>
      </div>

      {/* The quiet path. One line when nothing is wrong, which is almost
          always. */}
      {!rejecting ? (
        <FadeIn key="quiet">
        <button
          type="button"
          className="mt-3 text-xs text-on-surface-variant hover:text-primary underline underline-offset-2
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
          onClick={() => setRejecting(true)}
        >
          Some of these are not acceptable
        </button>
        </FadeIn>
      ) : (
        <FadeIn key="rejecting">
        <Panel
          level={3}
          title="Not acceptable"
          className="mt-3 space-y-3 ring-1 ring-amber-500/30"
          actions={(
            <button
              type="button"
              className={`${buttonClass('quiet', 'sm')} shrink-0`}
              onClick={() => {
                setRejecting(false);
                setRejectedQty('');
                setRejectionReason('');
              }}
            >
              Cancel
            </button>
          )}
        >
          <p className="text-[11px] text-on-surface-variant leading-relaxed">
            These still count as received, because the vendor shipped them. They go to QC Hold
            instead of stock, and a return is opened.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={labelClass}>How many are bad</span>
              <Field
                thumb
                type="number"
                min={1}
                max={Math.max(1, (parseInt(quantity, 10) || 1) - 1)}
                value={rejectedQty}
                onChange={(e) => setRejectedQty(e.target.value)}
                placeholder="0"
              />
            </label>
            <div className="flex items-end">
              {/* The arithmetic, done for them. Counting 40 and typing 6 should
                  not require working out what 34 is. */}
              <p className="text-xs text-on-surface-variant pb-2 leading-relaxed">
                {rejectedSplit
                  ? `${rejectedSplit.accepted} into stock, ${rejectedSplit.rejected} to QC Hold.`
                  : 'Enter how many, out of the total above.'}
              </p>
            </div>
          </div>

          <label className="block">
            <span className={labelClass}>What is wrong with them</span>
            <Field
              thumb
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Bent pins, water damage, dead at the flash station"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={labelClass}>What happens to them</span>
              <Select
                label="What happens to them"
                value={rejectionResolution}
                onChange={(key) => setRejectionResolution(key as 'return_to_vendor' | 'scrap')}
                options={[
                  { key: 'return_to_vendor', label: 'Send back to the vendor' },
                  { key: 'scrap', label: 'Scrap them' },
                ]}
              />
            </label>
          </div>
        </Panel>
        </FadeIn>
      )}

      </Section>

      {/* ── WHAT ARRIVED ─────────────────────────────────────────────── */}
      <Section title="What arrived" hint="off the box label">
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={labelClass}>Lot number</span>
          <Field
            thumb
            value={lotNumber}
            onChange={(e) => setLotNumber(e.target.value)}
            placeholder="From the box label"
          />
        </label>
        <label className="block">
          <span className={labelClass}>Manufacture date</span>
          <Field
            thumb
            type="date"
            value={manufactureDate}
            onChange={(e) => setManufactureDate(e.target.value)}
          />
        </label>
      </div>

      </Section>

      {/* ── WHERE AND WHEN ───────────────────────────────────────────── */}
      <Section title="Where it went">
      <div className="grid grid-cols-2 gap-3">
        <div className="block">
          <EntityCombobox
            label="Location"
            value={locationId}
            options={locationOptions}
            onSelect={(o) => setLocationId(o ? String(o.id) : '')}
            placeholder={locations.length ? 'Type to search' : 'No locations yet'}
            disabled={!locations.length}
            labelClassName={labelClass}
            emptyMessage="Nothing matches that."
            noOptionsMessage="No locations to receive into."
          />
          {/* The empty state Swarm actually sees on day one. An empty picker
              with no explanation is the thing that sent people to Slack. */}
          <Reveal show={locationsLoaded && locations.length === 0}>
            <Alert tone="info" variant="text" className="mt-1.5">
              Nowhere to receive into yet. An admin needs to add a warehouse under
              Settings, Locations before anything can be booked in.
            </Alert>
          </Reveal>
          {/* Vendor premises are a real destination and a one-way door today:
              we can record stock arriving at an assembler, but nothing yet
              records it being consumed there. Say so at the point of choosing. */}
          <Reveal show={selectedLocation?.type === 'vendor'}>
            <p className="text-xs text-on-surface-variant mt-1.5 leading-snug">
              This is {selectedLocation?.vendor_name || 'a vendor'}&apos;s premises, not your
              shelf. Stock filed here stays on the books but does not count toward what
              you can build.
            </p>
          </Reveal>
        </div>

        {isAdmin ? (
          <label className="block">
            <span className={labelClass}>
              Received on
              <span className="ml-2 normal-case tracking-normal text-on-surface-variant">
                (blank = today)
              </span>
            </span>
            <Field
              thumb
              type="date"
              max={todayIso()}
              value={receivedAt}
              onChange={(e) => setReceivedAt(e.target.value)}
            />
            <Reveal show={!!receivedAt && receivedAt !== todayIso()}>
              <p className="text-xs text-primary/90 mt-1.5 leading-snug">
                Backdating. The stock movement will be recorded on this date too, so it
                lands in the right month.
              </p>
            </Reveal>
          </label>
        ) : (
          <div />
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={labelClass}>Notes</span>
          <Field
            thumb
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
          />
        </label>
      </div>

      </Section>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className={buttonClass('tonal', 'md')}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className={buttonClass('filled', 'md')}
        >
          {submitting ? 'Receiving…' : 'Receive & Print'}
        </button>
      </div>
    </form>
    )}
    {pendingBody && (
      <ReceiverIdentityModal
        busy={submitting}
        error={identityError}
        onConfirm={({ user_id, pin }) => {
          setIdentityError(null);
          submitReceive({ ...pendingBody, received_as: { user_id, pin } });
        }}
        onClose={() => {
          setPendingBody(null);
          setIdentityError(null);
        }}
      />
    )}
    {mintOpen && (
      <NewItemModal
        initialName={mintName}
        onClose={() => {
          setMintOpen(false);
          // Cleared on the way out, both ways. A name left behind would seed the
          // NEXT mint with a word from a search nobody is running any more.
          setMintName('');
        }}
        onMinted={(minted) => {
          // Straight back into the form with it chosen, which is the whole
          // point — the alternative was leaving the drawer, minting elsewhere,
          // and retyping everything.
          setMintOpen(false);
          setMintName('');
          setSelectedPart(minted);
          toast.success(`${itemLabel(minted)} added. Now receive it.`);
        }}
      />
    )}
    </>
  );
};

export default ReceiveDrawer;
